/**
 * Guards deciding when a global key must be left alone.
 *
 * Two distinct cases, both of which have historically been sources of bugs:
 *  - the user is typing (Space must insert a space);
 *  - the user has focused a control that natively activates on Space
 *    (button, link, checkbox). Stealing Space there breaks keyboard access.
 */

/**
 * Is this element itself a place text goes?
 *
 * Separate from `isTypingTarget` because the on-screen keyboard clause below is
 * about the *page*, not the element: the maths composer has to ask "is the key
 * destined for some other input" while its own keyboard panel is on screen, and
 * the page-wide test would answer yes to every key.
 */
export function isEditableElement(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el || typeof el !== "object") return false;

  const tag = el.tagName;
  if (tag === "TEXTAREA") return true;
  if (tag === "SELECT") return true;
  if (tag === "INPUT") {
    const type = (el as HTMLInputElement).type;
    // Buttons/checkboxes are handled by isKeyboardActivatableTarget instead.
    return !["button", "submit", "reset", "checkbox", "radio", "file"].includes(
      type,
    );
  }
  // isContentEditable resolves inheritance in a browser but is unimplemented in
  // jsdom, so the attribute is checked too. "false" explicitly opts out.
  if (el.isContentEditable) return true;
  const editable = el.getAttribute?.("contenteditable");
  if (editable != null && editable !== "false") return true;

  // AST-backed maths fields (the chat composer, the exit-ticket fields) are a
  // focusable div, not a real control: no tagName that matches and no
  // isContentEditable. They declare role="textbox" instead, and a key typed into one
  // belongs to that field, not to hold-to-talk.
  return el.getAttribute?.("role") === "textbox";
}

export function isTypingTarget(target: EventTarget | null): boolean {
  if (isEditableElement(target)) return true;

  // The app renders its own on-screen keyboard; while it is up, physical keys
  // belong to it wherever they were aimed.
  if (typeof document !== "undefined") {
    if (document.querySelector('[data-custom-keyboard="true"]')) return true;
  }
  return false;
}

export function isKeyboardActivatableTarget(
  target: EventTarget | null,
): boolean {
  const el = target as HTMLElement | null;
  if (!el || typeof el !== "object" || typeof el.closest !== "function") {
    return false;
  }
  return Boolean(
    el.closest(
      'button, a[href], summary, [role="button"], [role="link"], [role="checkbox"], [role="switch"], [role="tab"], [role="menuitem"], input[type="button"], input[type="submit"], input[type="reset"], input[type="checkbox"], input[type="radio"]',
    ),
  );
}

interface ClaimOptions {
  /** `KeyboardEvent.code` values this shortcut answers to. */
  codes: readonly string[];
  /**
   * Whether to stand down on a focused button/link. True for keys the browser
   * itself uses to activate controls (Space, Enter); false for keys that
   * activate nothing (S), where a focused button is irrelevant.
   */
  respectActivatableTarget?: boolean;
  /** Override the typing test — the tutor also knows about its math keyboard. */
  isTyping?: (target: EventTarget | null) => boolean;
}

/**
 * The single admission test for a global key shortcut.
 *
 * `e.repeat` is deliberately NOT checked here: Space must still
 * `preventDefault()` on auto-repeat so the page never scrolls mid-stroke,
 * while the other keys simply ignore repeats. That difference belongs to the
 * caller.
 */
export function canClaimGlobalKey(
  e: KeyboardEvent,
  {
    codes,
    respectActivatableTarget = true,
    isTyping = isTypingTarget,
  }: ClaimOptions,
): boolean {
  if (!codes.includes(e.code)) return false;
  // A shortcut is a bare key press. Cmd/Ctrl/Alt combinations belong to the
  // browser or the OS (Cmd+Space is Spotlight and the input-source switcher).
  if (e.metaKey || e.ctrlKey || e.altKey) return false;
  // IME composition — never intercept mid-composition.
  if (e.isComposing) return false;
  if (isTyping(e.target)) return false;
  if (respectActivatableTarget && isKeyboardActivatableTarget(e.target)) {
    return false;
  }
  return true;
}
